import { BALL, FIELD, GK, KICK, PHYSICS, goalMouth, modeSpeed, type Ball, type Player } from "@golazo/shared";
import { clamp, clampLength, closestPointOnSegment, len2 } from "../core/math.js";
import { signed, type Random } from "../core/random.js";
import { ballClearsPlayers } from "./loft.js";
import { isBallHeadingToGoal, isInSaveZone, predictGoalLineCrossing } from "./ballPrediction.js";
import { sweepRelative } from "./sweep.js";

/**
 * Calidad de la parada segun potencia del disparo:
 *  - suave → atrapada (balon a los pies)
 *  - media → desvio controlado
 *  - misil → fumble: la inercia suelta el balon en el area
 */
function saveFeel(speed: number): { absorb: number; restitution: number; fumble: boolean; catch: boolean } {
  if (speed >= GK.FUMBLE_SPEED) {
    return {
      absorb: GK.FUMBLE_ABSORB,
      restitution: GK.FUMBLE_RESTITUTION,
      fumble: true,
      catch: false,
    };
  }
  if (speed <= GK.CATCH_SPEED) {
    return {
      absorb: GK.CATCH_ABSORB,
      restitution: GK.CATCH_RESTITUTION,
      fumble: false,
      catch: true,
    };
  }
  return {
    absorb: GK.ABSORB,
    restitution: GK.DEFLECT_RESTITUTION,
    fumble: false,
    catch: false,
  };
}

/**
 * Punta del brazo estirado, en coordenadas de mundo. Sin estirada el brazo no
 * existe y la punta es el propio cuerpo: el alcance vuelve a ser un circulo.
 */
export function armTip(gk: Player): { x: number; y: number } {
  if (gk.diveTime <= 0) return { x: gk.pos.x, y: gk.pos.y };
  const reach = gk.radius * (1 + GK.ARM_SCALE);
  return {
    x: gk.pos.x + gk.diveDir.x * reach,
    y: gk.pos.y + gk.diveDir.y * reach,
  };
}

/**
 * Decide si el portero se lanza, y hacia donde.
 *
 * Tres condiciones, y las tres importan:
 *
 *  1. **Que el disparo vaya dentro.** Lanzarse a un balon que se va fuera es
 *     regalar el rechace.
 *  2. **Que NO llegue de pie.** Si el balon cruza a menos de `DIVE_TRIGGER_GAP`
 *     de su alcance normal, se queda plantado: un portero que se tira a todo se
 *     ve ridiculo y ademas acaba en el suelo cuando llega el rechace.
 *  3. **Que no venga de otra estirada.** El enfriamiento es el precio de
 *     equivocarse de lado, y es lo unico que separa esto de un muro.
 *
 * Y **se equivoca**: el lado se elige con `GK.DIVE_READ_ERROR` de error, de una
 * sola tirada del generador sembrado del partido. Sin eso la estirada es una
 * lectura perfecta y el disparo deja de decidir nada.
 */
export function tryGoalkeeperDive(ball: Ball, gk: Player, random: Random): boolean {
  if (gk.role !== "gk") return false;
  if (gk.diveTime > 0 || gk.diveCooldown > 0) return false;
  if (ball.unstoppable) return false;
  if (ball.loft > GK.CHIP_CLEAR_HEIGHT) return false;
  if (!isBallHeadingToGoal(ball, gk.side)) return false;

  const speed = len2(ball.vel.x, ball.vel.y);
  if (speed < GK.THREAT_MIN_SPEED) return false;

  const dx = ball.pos.x - gk.pos.x;
  const dy = ball.pos.y - gk.pos.y;
  if (len2(dx, dy) > GK.DIVE_MAX_BALL_DIST) return false;

  const goalX = gk.side === "home" ? 0 : FIELD.WIDTH;
  const crossing = predictGoalLineCrossing(ball, goalX);
  if (crossing === null) return false;

  const mouth = goalMouth();
  if (crossing < mouth.top || crossing > mouth.bottom) return false;

  // ¿Llega de pie? El alcance parado es cuerpo + la holgura de siempre.
  const standing = gk.radius + ball.radius + GK.SAVE_PAD;
  const gap = Math.abs(crossing - gk.pos.y) - standing;
  if (gap < GK.DIVE_TRIGGER_GAP) return false;

  const read = crossing + signed(random) * GK.DIVE_READ_ERROR;
  const toX = goalX - gk.pos.x;
  const toY = read - gk.pos.y;
  const len = len2(toX, toY) || 1;
  gk.diveDir.x = toX / len;
  gk.diveDir.y = toY / len;
  gk.diveTime = GK.DIVE_TIME;
  gk.diveCooldown = GK.DIVE_TIME + GK.DIVE_COOLDOWN;
  const leap = modeSpeed(GK.DIVE_SPEED);
  gk.vel.x = gk.diveDir.x * leap;
  gk.vel.y = gk.diveDir.y * leap;
  return true;
}

/**
 * Parada del portero. Absorbe la componente del balon hacia porteria y lo
 * desvia hacia fuera. Radio de alcance mayor que el cuerpo: estirada a palos.
 *
 * Devuelve `true` si ha habido contacto de parada este tick.
 */
/**
 * Altura a la que llega el portero ahora mismo.
 *
 * De pie, `CHIP_CLEAR_HEIGHT`; en el aire, eso mas `JUMP_REACH`. Es lo unico
 * que cambia el salto: alcance a lo ALTO. A lo ancho no gana ni un dedo — ver
 * `GK.JUMP_REACH`, y `tryGoalkeeperSave`, donde saltando no se aplica ni el
 * brazo ni el anillo.
 */
function saveCeiling(gk: Player): number {
  return gk.jumpTime > 0 ? GK.CHIP_CLEAR_HEIGHT + GK.JUMP_REACH : GK.CHIP_CLEAR_HEIGHT;
}

/**
 * Altura del balon cuando llegue a la altura del PORTERO, y cuanto falta.
 *
 * A la del portero, no a la de la linea de gol. El portero sale ~78 unidades a
 * por el balon, y una vaselina le pasa por encima ahi — para cuando el balon
 * cruza la linea ya ha caido. Leyendolo en la linea, el salto no se disparaba
 * practicamente nunca: medido, la conversion de vaselinas era identica con
 * salto y sin el.
 *
 * Un balon puede ir altisimo y estar cayendo, y al reves. Lo que decide es la
 * altura EN EL PUNTO DE CRUCE.
 */
function loftAtPlayer(ball: Ball, gk: Player): { loft: number; time: number } | null {
  const vx = ball.vel.x;
  if (Math.abs(vx) < 1e-3) return null;
  const t = (gk.pos.x - ball.pos.x) / vx;
  if (t <= 0 || t > 1.5) return null;
  const h = ball.loft + ball.loftVel * t - 0.5 * BALL.LOFT_GRAVITY * t * t;
  return { loft: Math.max(0, h), time: t };
}

/**
 * SALTO. Para las vaselinas que le pasarian justo por encima.
 *
 * Es el gesto complementario a la estirada: aquella gana ancho, este gana
 * alto. Antes, todo lo que superara `CHIP_CLEAR_HEIGHT` era gol sin mas, o sea
 * que la vaselina no era un recurso sino un boton de marcar.
 *
 * Las condiciones son las que impiden que se convierta en un muro:
 *
 *  1. **Que vaya dentro** y que llegue por ALTO (`JUMP_MIN_LOFT`): a lo que
 *     llega de pie no se salta.
 *  2. **Que siga estando a su alcance al cruzar.** Se lee la altura en la
 *     linea, no la de ahora.
 *  3. **Que le de tiempo.** Salta cuando el balon esta a menos de medio vuelo:
 *     saltar pronto es caer antes de que llegue.
 *  4. **Y se equivoca**, y despues se queda en el suelo mas tiempo que tras
 *     una estirada. El rechace de un salto es de quien lo cace.
 */
export function tryGoalkeeperJump(ball: Ball, gk: Player, random: Random): boolean {
  if (gk.role !== "gk") return false;
  if (gk.jumpTime > 0 || gk.jumpCooldown > 0) return false;
  if (gk.diveTime > 0) return false;
  if (ball.unstoppable) return false;
  if (!isBallHeadingToGoal(ball, gk.side)) return false;

  const speed = len2(ball.vel.x, ball.vel.y);
  if (speed < GK.THREAT_MIN_SPEED) return false;

  const dx = ball.pos.x - gk.pos.x;
  const dy = ball.pos.y - gk.pos.y;
  if (len2(dx, dy) > GK.JUMP_MAX_BALL_DIST) return false;

  const goalX = gk.side === "home" ? 0 : FIELD.WIDTH;
  const crossing = predictGoalLineCrossing(ball, goalX);
  if (crossing === null) return false;

  const arriving = loftAtPlayer(ball, gk);
  if (arriving === null) return false;
  /*
   * Solo salta a lo que NO alcanzaria de pie.
   *
   * El primer intento usaba un umbral propio mas bajo, y salio caro: un
   * disparo raso fuerte lleva algo de altura, asi que el portero saltaba a
   * balones que habria parado plantado — y saltando pierde el anillo de
   * alcance lateral. Medido: el 11v11 paso de encajar el 10,8% al 18,3%. El
   * salto estaba QUITANDO paradas.
   */
  if (arriving.loft <= GK.CHIP_CLEAR_HEIGHT) return false;
  if (arriving.loft > GK.CHIP_CLEAR_HEIGHT + GK.JUMP_REACH) return false;

  // Que le de tiempo a estar ARRIBA cuando llegue, no subiendo o cayendo.
  if (arriving.time > GK.JUMP_TIME * 0.7) return false;

  const read = crossing + signed(random) * GK.JUMP_READ_ERROR;
  const toX = goalX - gk.pos.x;
  const toY = read - gk.pos.y;
  const len = len2(toX, toY) || 1;
  gk.jumpTime = GK.JUMP_TIME;
  gk.jumpCooldown = GK.JUMP_TIME + GK.JUMP_COOLDOWN;
  // Impulso pequeño y sobre todo hacia la linea: el salto es vertical, y en
  // cenital lo que se ve es un paso corto hacia el balon.
  const leap = modeSpeed(GK.JUMP_SPEED);
  gk.vel.x = (toX / len) * leap;
  gk.vel.y = (toY / len) * leap;
  return true;
}

export function tryGoalkeeperSave(ball: Ball, gk: Player): boolean {
  if (ball.unstoppable) return false;
  if (gk.role !== "gk") return false;
  // Vaselina: por encima de su alcance. Saltando, ese alcance sube.
  if (ball.loft > saveCeiling(gk)) return false;
  if (!isBallHeadingToGoal(ball, gk.side)) return false;
  if (!isInSaveZone(ball, gk.side)) return false;

  const speed = len2(ball.vel.x, ball.vel.y);
  if (speed < GK.THREAT_MIN_SPEED) return false;

  // El alcance no es un circulo alrededor del cuerpo: es una CAPSULA desde el
  // cuerpo hasta la punta del brazo. Sin estirada los dos puntos coinciden y
  // vuelve a ser un circulo, que es como se comportaba antes.
  const tip = armTip(gk);
  const near = closestPointOnSegment(ball.pos, gk.pos, tip);

  let nx = ball.pos.x - near.x;
  let ny = ball.pos.y - near.y;
  let dist = len2(nx, ny);

  // El anillo de alcance de siempre (`DIVE_REACH`) y el brazo NO se suman: era
  // la estirada vieja, la que existia porque no habia estirada de verdad. Con
  // el brazo fuera se queda solo la holgura de contacto.
  const bodyDist = len2(ball.pos.x - gk.pos.x, ball.pos.y - gk.pos.y);
  const closeness = clamp(1 - bodyDist / 300, 0, 1);
  const urgency = clamp(speed / KICK.SHOOT_SPEED, 0, 1);
  const reach =
    gk.diveTime > 0 || gk.jumpTime > 0
      ? GK.SAVE_PAD
      : GK.SAVE_PAD + GK.DIVE_REACH * closeness * urgency;

  // El brazo ADELGAZA hacia la mano. Con grosor constante no era un brazo,
  // era una pared: ver `GK.HAND_SCALE`.
  const armLen = len2(tip.x - gk.pos.x, tip.y - gk.pos.y);
  const along =
    armLen > 1e-6
      ? len2(near.x - gk.pos.x, near.y - gk.pos.y) / armLen
      : 0;
  const hand = ball.radius * GK.HAND_SCALE;
  const thickness = gk.radius + (hand - gk.radius) * along;
  const saveRadius = thickness + ball.radius + reach;

  if (dist >= saveRadius) return false;

  if (dist < 1e-6) {
    nx = gk.side === "home" ? 1 : -1;
    ny = 0;
    dist = 1;
  } else {
    nx /= dist;
    ny /= dist;
  }

  const feel = saveFeel(speed);

  // Atrapada limpia: balon a los pies.
  if (feel.catch) {
    const hold = gk.radius + ball.radius + 2;
    const out = gk.side === "home" ? 1 : -1;
    ball.pos.x = gk.pos.x + out * hold;
    ball.pos.y = gk.pos.y;
    // Atrapar corta el vuelo: el portero cae con el balon.
    gk.diveTime = 0;
    ball.vel.x = 0;
    ball.vel.y = 0;
    ball.spin = 0;
    ball.topSpin = 0;
    ball.loft = 0;
    ball.prevLoft = 0;
    ball.loftVel = 0;
    return true;
  }

  const goalDirX = gk.side === "home" ? -1 : 1;
  const goalward = ball.vel.x * goalDirX;
  if (goalward > 0) {
    ball.vel.x -= goalward * feel.absorb * goalDirX;
    ball.vel.y *= 1 - feel.absorb * (feel.fumble ? 0.15 : 0.4);
  }

  ball.pos.x = near.x + nx * saveRadius;
  ball.pos.y = near.y + ny * saveRadius;

  const rvx = ball.vel.x - gk.vel.x;
  const rvy = ball.vel.y - gk.vel.y;
  const vn = rvx * nx + rvy * ny;
  if (vn < 0) {
    const impulse = -(1 + feel.restitution) * vn;
    ball.vel.x += impulse * nx;
    ball.vel.y += impulse * ny;
  }

  if (feel.fumble) {
    const intoBox = -goalDirX;
    ball.vel.x += intoBox * speed * 0.12;
    ball.vel.y += (ball.pos.y < FIELD.HEIGHT / 2 ? 1 : -1) * speed * 0.04;
    ball.spin += (Math.sign(ball.vel.y) || 1) * 0.35;
  }

  // Desviar HACIA FUERA. El rebote por la normal, tal cual, devolvia el balon
  // muchas veces a los pies del que acababa de disparar: el paradon se leia
  // como un regalo. Aqui se le añade componente hacia la banda mas cercana,
  // que es lo que hace un portero de verdad — y de paso convierte la parada en
  // una jugada nueva (rechace lateral) en vez de un rebote esteril.
  const away = ball.pos.y < FIELD.HEIGHT / 2 ? -1 : 1;
  const wide = speed * GK.DEFLECT_WIDE * (feel.fumble ? 0.5 : 1);
  ball.vel.y += away * wide * 0.5;
  ball.vel.x += -goalDirX * wide * 0.25;

  clampLength(ball.vel, BALL.MAX_SPEED);
  return true;
}

/**
 * Colision balon-jugador, CONTINUA. El portero usa parada especial cuando el
 * balon amenaza su porteria; el resto rebota elasticamente (desvios de disparo).
 *
 * Antes preguntaba solo "¿solapan al final del tick?", y a 60 Hz un disparo a
 * tope (1.760 u/s + herencia de carrera) avanza 31 u mientras el diametro de
 * colision es 34: medido, el 18-24% de los disparos potentes atravesaba
 * limpiamente el cuerpo del defensor. Ahora se barre el segmento recorrido.
 *
 * El jugador tambien se mueve, asi que el barrido es relativo: se resta su
 * desplazamiento del del balon.
 */
export function collideBallWithPlayers(
  ball: Ball,
  players: readonly Player[],
  skipId: string | null,
): void {
  for (const player of players) {
    if (player.id === skipId) continue;

    if (player.role === "gk" && tryGoalkeeperSave(ball, player)) continue;
    if (player.role !== "gk" && ballClearsPlayers(ball)) continue;

    const minDist = player.radius + ball.radius;
    const hit = sweepRelative(ball.prevPos, ball.pos, player.prevPos, player.pos, minDist);
    if (!hit) continue;

    let nx = hit.nx;
    let ny = hit.ny;
    if (!Number.isFinite(nx) || !Number.isFinite(ny) || (nx === 0 && ny === 0)) {
      nx = player.facing.x || 1;
      ny = player.facing.y;
      const len = len2(nx, ny) || 1;
      nx /= len;
      ny /= len;
    }

    // Se recoloca en el punto de contacto, no donde acabo el tick: si no, un
    // balon interceptado a mitad de recorrido aparecia al otro lado del cuerpo.
    ball.pos.x = player.pos.x + nx * minDist;
    ball.pos.y = player.pos.y + ny * minDist;

    const rvx = ball.vel.x - player.vel.x;
    const rvy = ball.vel.y - player.vel.y;
    const vn = rvx * nx + rvy * ny;
    if (vn >= 0) continue;

    const impulse = -(1 + BALL.PLAYER_RESTITUTION) * vn;
    ball.vel.x += impulse * nx;
    ball.vel.y += impulse * ny;
    const tangent = rvx * -ny + rvy * nx;
    ball.spin += clamp(tangent * 0.0018, -0.55, 0.55);
    clampLength(ball.vel, BALL.MAX_SPEED);

    // Resto del tick con la velocidad ya rebotada: el contacto ocurrio en `t`,
    // no al final, y sin esto el balon perderia ese tramo de recorrido.
    const remaining = 1 - hit.t;
    if (remaining > 0) {
      ball.pos.x += ball.vel.x * PHYSICS.DT * remaining;
      ball.pos.y += ball.vel.y * PHYSICS.DT * remaining;
    }
  }
}
